import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  createComponent,
  DestroyRef,
  EnvironmentInjector,
  inject,
  Injectable,
  type TemplateRef,
} from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService, type NzNotificationDataOptions } from 'ng-zorro-antd/notification';
import { AE_OVERLAY_CLASS, APP_CONFIG } from '../config/app-config';
import { HTTP_MESSAGES } from '../i18n/http.messages';
import { t } from '../i18n/t';
import { ApiError } from './api-error';
import {
  NotificationTemplatesComponent,
  type NoticeContext,
} from './notification-templates.component';

/** Clase de las notificaciones de error de la feature: oculta el enlace de cierre de NG-ZORRO (`styles/`). */
export const NOTICE_CLASS = 'ae-notice';

/**
 * `nzKey` de todos los fallos de red (status 0, también el timeout): si la API no responde fallan a la vez todas las
 * peticiones de la pantalla y deben dejar una sola notificación (NG-ZORRO sustituye el contenido de la que tiene la
 * misma clave), no una por URL.
 */
export const NETWORK_NOTICE_KEY = 'api-error-network';

/** Origen de una URL absoluta `http(s)`; null si es relativa o no se puede leer. */
function absoluteOrigin(url: string | null | undefined): string | null {
  if (!url || !/^https?:\/\//i.test(url)) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Una sola forma de notificar (ux-spec §8.9): `nz-message` 4 s para éxitos, `nz-notification` persistente
 * para errores con el mensaje de la API y el id de petición. Los componentes no llaman a NG-ZORRO directamente.
 *
 * Sirius no usa `provideNzConfig`, así que la colocación (`topRight`, la de Sirius), la duración y la clase
 * `ae-overlay` van por llamada (`nzMaxStack` no existe por llamada: queda el de NG-ZORRO).
 *
 * Accesibilidad (WCAG 4.1.3 / 2.1.1): NG-ZORRO no pone `role` ni `aria-live` en sus toasts, así que cada texto se
 * anuncia con el `LiveAnnouncer` de la CDK (éxitos e informes en `polite`, errores en `assertive`). Las notificaciones
 * de error llevan un botón «Cerrar» real en lugar del enlace sin `href` de NG-ZORRO, y `Esc` cierra la que tenga el foco.
 */
@Injectable({ providedIn: 'root' })
export class ApiNotificationsService {
  private readonly message = inject(NzMessageService);
  private readonly notification = inject(NzNotificationService);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly config = inject(APP_CONFIG);
  private templates: NotificationTemplatesComponent | null = null;

  constructor() {
    const document = inject(DOCUMENT);
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const notice = (event.target as Element | null)?.closest?.(`.${NOTICE_CLASS}`);
      const close = notice?.querySelector<HTMLButtonElement>(`.${NOTICE_CLASS}__close`);
      if (!close) return;
      event.preventDefault();
      event.stopPropagation();
      close.click();
    };
    document.addEventListener('keydown', onKeydown, true);
    inject(DestroyRef).onDestroy(() => document.removeEventListener('keydown', onKeydown, true));
  }

  success(text: string): void {
    this.message.success(text, { nzDuration: 4000 });
    this.announce(text, 'polite');
  }

  info(text: string): void {
    this.message.info(text, { nzDuration: 4000 });
    this.announce(text, 'polite');
  }

  /**
   * Error de la API con código, acción sugerida y `x-request-id`. Una notificación por estado y URL; los fallos de
   * red comparten una sola (`NETWORK_NOTICE_KEY`).
   */
  error(error: ApiError, title = HTTP_MESSAGES.errors.title): void {
    const body = this.describe(error);
    this.notification.error(title, body, {
      nzDuration: 0,
      nzKey: error.isNetwork ? NETWORK_NOTICE_KEY : `api-error-${error.status}-${error.url ?? ''}`,
      ...this.closable(),
    });
    this.announce(`${title}. ${body}`, 'assertive');
  }

  forbidden(error: ApiError): void {
    const scope = error.missingScopes.filter(s => s !== 'admin')[0];
    const body = scope
      ? t(HTTP_MESSAGES.errors.forbidden, { scope })
      : HTTP_MESSAGES.errors.forbiddenGeneric;
    const text = this.withRequestId(body, error);
    this.notification.warning(HTTP_MESSAGES.errors.forbiddenTitle, text, {
      nzDuration: 8000,
      nzKey: 'api-forbidden',
      nzPauseOnHover: true,
      ...this.closable(),
    });
    this.announce(`${HTTP_MESSAGES.errors.forbiddenTitle}. ${text}`, 'assertive');
  }

  /**
   * Texto de un error para una notificación o un `ErrorState`: qué pasó · por qué · qué hacer. Sin el detalle del
   * transporte de un fallo de red (`ApiError.detailMessages`).
   */
  describe(error: ApiError): string {
    const what = this.summary(error);
    const messages = error.detailMessages;
    const detail =
      messages.length > 0 && messages[0] !== what ? messages.slice(0, 3).join(' · ') : '';
    return this.withRequestId([what, detail].filter(part => part.length > 0).join('\n'), error);
  }

  /**
   * Texto de una línea para un error que se enseña inline (alerta de un formulario, fila de una importación): los
   * mensajes de la API tal cual; en un fallo de red, `describe()` («No se pudo contactar con la API en …», el aviso de
   * un timeout), nunca el «Network error» crudo del transporte.
   */
  inline(error: ApiError): string {
    return error.isNetwork
      ? this.describe(error).replace(/\n+/g, ' · ')
      : error.messages.join(' · ');
  }

  summary(error: ApiError): string {
    if (error.isNetwork) return t(HTTP_MESSAGES.errors.network, { origin: this.apiOrigin(error) });
    switch (error.status) {
      case 400:
        return HTTP_MESSAGES.errors.validation;
      case 401:
        return HTTP_MESSAGES.errors.unauthorized;
      case 403:
        return HTTP_MESSAGES.errors.forbiddenGeneric;
      case 404:
        return HTTP_MESSAGES.errors.notFound;
      case 409:
        return HTTP_MESSAGES.errors.conflict;
      case 422:
        return HTTP_MESSAGES.errors.validation;
      case 429:
        return HTTP_MESSAGES.errors.rateLimited;
      default:
        return error.status >= 500
          ? t(HTTP_MESSAGES.errors.server, { status: error.status })
          : error.message || HTTP_MESSAGES.errors.unknown;
    }
  }

  /**
   * Origen de la API que falló, no el de la página (apps/web llama a la API cross-origin): el de la URL de la
   * petición si es absoluta; si no, el de `apiBaseUrl`; con una base relativa (specs), el de la página.
   */
  private apiOrigin(error: ApiError): string {
    return (
      absoluteOrigin(error.url) ??
      absoluteOrigin(this.config.apiBaseUrl) ??
      (typeof location !== 'undefined' ? location.origin : '')
    );
  }

  /** Lectores de pantalla: los toasts de NG-ZORRO no son regiones vivas. */
  announce(text: string, politeness: 'polite' | 'assertive'): void {
    void this.announcer.announce(text.replace(/\n+/g, '. '), politeness);
  }

  private withRequestId(text: string, error: ApiError): string {
    return error.requestId
      ? `${text}\n${t(HTTP_MESSAGES.errors.requestId, { requestId: error.requestId })}`
      : text;
  }

  /**
   * Opciones comunes de las notificaciones: arriba a la derecha (como Sirius), clase `ae-overlay` y, si hay
   * plantilla, el botón «Cerrar» propio (que además oculta el enlace de NG-ZORRO con `ae-notice`).
   */
  private closable(): Pick<
    NzNotificationDataOptions<NoticeContext>,
    'nzPlacement' | 'nzClass' | 'nzButton'
  > {
    const template = this.closeTemplate();
    return template
      ? {
          nzPlacement: 'topRight',
          nzClass: `${AE_OVERLAY_CLASS} ${NOTICE_CLASS}`,
          nzButton: template,
        }
      : { nzPlacement: 'topRight', nzClass: AE_OVERLAY_CLASS };
  }

  private closeTemplate(): TemplateRef<NoticeContext> | null {
    try {
      if (!this.templates) {
        const ref = createComponent(NotificationTemplatesComponent, {
          environmentInjector: this.injector,
        });
        this.appRef.attachView(ref.hostView);
        ref.changeDetectorRef.detectChanges();
        this.templates = ref.instance;
      }
      return this.templates.close();
    } catch {
      // Sin plantilla (entornos de prueba mínimos): queda el cierre de NG-ZORRO.
      return null;
    }
  }
}
